// app/frontend/components/Eventform/utils/timezoneHelpers.ts

export interface TimezoneOption {
  value: string;
  label: string;
}

export interface RailsTimezoneData {
  value: string;
  label: string;
}

// Convert Rails timezone data to Mantine-compatible format
export const processRailsTimezoneData = (railsData: RailsTimezoneData[]): TimezoneOption[] => {
  console.log('processRailsTimezoneData called with:', railsData);
  console.log('First entry type check:', typeof railsData[0], railsData[0]);
  console.log('First entry value:', railsData[0]?.value, typeof railsData[0]?.value);
  console.log('First entry label:', railsData[0]?.label, typeof railsData[0]?.label);

  // Check for any invalid entries
  const invalidEntries = railsData.filter((tz) => !tz.value || typeof tz.value !== 'string' || tz.value.trim() === '');
  if (invalidEntries.length > 0) {
    console.error('Found invalid timezone entries:', invalidEntries);
  }

  const processed = railsData
    .map((tz, index) => {
      // Validate each entry
      if (!tz.value || typeof tz.value !== 'string') {
        console.error(`Invalid timezone at index ${index}:`, tz);
        return null;
      }

      if (tz.value.trim() === '') {
        console.error(`Empty timezone value at index ${index}:`, tz);
        return null;
      }

      const result = {
        value: tz.value.trim(),
        label: tz.label || tz.value,
      };

      // Log first few for verification
      if (index < 5) {
        console.log(`Processed timezone ${index}:`, result);
      }

      return result;
    })
    .filter(Boolean) as TimezoneOption[];

  console.log(`Successfully processed ${processed.length} timezones`);

  // Final validation - check if any processed entries are invalid
  const finalInvalid = processed.filter((tz) => !tz.value || tz.value.trim() === '');
  if (finalInvalid.length > 0) {
    console.error('CRITICAL: Invalid entries after processing:', finalInvalid);
  }

  return processed;
};

// Find Rails timezone name from TimeZoneDB result
export const findRailsTimezone = (timezoneName: string, railsTimezones: TimezoneOption[]): string | null => {
  console.log('Looking for timezone:', timezoneName, 'in', railsTimezones.length, 'Rails timezones');

  if (!timezoneName || !railsTimezones || railsTimezones.length === 0) {
    console.warn('Invalid parameters for findRailsTimezone:', {
      timezoneName,
      railsTimezonesCount: railsTimezones?.length,
    });
    return null;
  }

  // Direct match by value
  const directMatch = railsTimezones.find((tz) => tz.value === timezoneName);
  if (directMatch) {
    console.log('Found direct match:', directMatch.value);
    return directMatch.value;
  }

  // Convert IANA format to Rails format for comparison
  const railsFormat = convertIANAToRails(timezoneName);
  console.log('Converted IANA to Rails format:', timezoneName, '->', railsFormat);

  const convertedMatch = railsTimezones.find((tz) => tz.value === railsFormat);
  if (convertedMatch) {
    console.log('Found converted match:', convertedMatch.value);
    return convertedMatch.value;
  }

  // Partial matching as fallback
  const partialMatch = railsTimezones.find(
    (tz) =>
      tz.value.toLowerCase().includes(timezoneName.toLowerCase()) ||
      timezoneName.toLowerCase().includes(tz.value.toLowerCase()),
  );

  if (partialMatch) {
    console.log('Found partial match:', partialMatch.value);
    return partialMatch.value;
  }

  console.warn('No match found for timezone:', timezoneName);
  return null;
};

// Convert IANA timezone names to Rails format
// Convert IANA timezone names to Rails format
// Convert IANA timezone names to Rails format
export const convertIANAToRails = (ianaName: string): string => {
  const conversionMap: Record<string, string> = {
    // === PACIFIC ISLANDS & INTERNATIONAL DATE LINE ===
    'Pacific/Kiritimati': 'International Date Line West',
    'Pacific/Midway': 'Midway Island',
    'Pacific/Pago_Pago': 'American Samoa',
    'US/Samoa': 'American Samoa',

    // === NORTH AMERICA ===
    // United States
    'Pacific/Honolulu': 'Hawaii',
    'US/Hawaii': 'Hawaii',
    'America/Anchorage': 'Alaska',
    'US/Alaska': 'Alaska',
    'America/Los_Angeles': 'Pacific Time (US & Canada)',
    'America/Vancouver': 'Pacific Time (US & Canada)',
    'US/Pacific': 'Pacific Time (US & Canada)',
    'America/Tijuana': 'Tijuana',
    'America/Phoenix': 'Arizona',
    'US/Arizona': 'Arizona',
    'America/Mazatlan': 'Mazatlan',
    'America/Denver': 'Mountain Time (US & Canada)',
    'America/Edmonton': 'Mountain Time (US & Canada)',
    'US/Mountain': 'Mountain Time (US & Canada)',

    // Central Time Zone
    'America/Guatemala': 'Central America',
    'America/Belize': 'Central America',
    'America/Costa_Rica': 'Central America',
    'America/El_Salvador': 'Central America',
    'America/Managua': 'Central America',
    'America/Tegucigalpa': 'Central America',
    'America/Chicago': 'Central Time (US & Canada)',
    'America/Winnipeg': 'Central Time (US & Canada)',
    'US/Central': 'Central Time (US & Canada)',
    'America/Chihuahua': 'Chihuahua',
    'America/Hermosillo': 'Chihuahua',
    'America/Guadalajara': 'Guadalajara',
    'America/Mexico_City': 'Mexico City',
    'America/Monterrey': 'Monterrey',
    'America/Regina': 'Saskatchewan',

    // Eastern Time Zone
    'America/Bogota': 'Bogota',
    'America/Lima': 'Lima',
    'America/Quito': 'Quito',
    'America/New_York': 'Eastern Time (US & Canada)',
    'America/Toronto': 'Eastern Time (US & Canada)',
    'America/Montreal': 'Eastern Time (US & Canada)',
    'America/Detroit': 'Eastern Time (US & Canada)',
    'US/Eastern': 'Eastern Time (US & Canada)',
    'America/Indiana/Indianapolis': 'Indiana (East)',
    'US/East-Indiana': 'Indiana (East)',

    // Atlantic Time Zone
    'America/Halifax': 'Atlantic Time (Canada)',
    'America/Glace_Bay': 'Atlantic Time (Canada)',
    'America/Moncton': 'Atlantic Time (Canada)',
    'America/Caracas': 'Caracas',
    'America/Guyana': 'Georgetown',
    'America/Georgetown': 'Georgetown',
    'America/La_Paz': 'La Paz',
    'America/Puerto_Rico': 'Puerto Rico',
    'America/Santiago': 'Santiago',

    // Newfoundland
    'America/St_Johns': 'Newfoundland',

    // South America
    'America/Sao_Paulo': 'Brasilia',
    'America/Bahia': 'Brasilia',
    'America/Fortaleza': 'Brasilia',
    'America/Recife': 'Brasilia',
    'America/Argentina/Buenos_Aires': 'Buenos Aires',
    'America/Buenos_Aires': 'Buenos Aires',
    'America/Montevideo': 'Montevideo',

    // Atlantic Islands
    'America/Godthab': 'Greenland',
    'America/Danmarkshavn': 'Greenland',
    'Atlantic/South_Georgia': 'Mid-Atlantic',
    'Atlantic/Azores': 'Azores',
    'Atlantic/Cape_Verde': 'Cape Verde Is.',

    // === AFRICA ===
    'Africa/Casablanca': 'Casablanca',
    'Africa/El_Aaiun': 'Casablanca',
    'Africa/Monrovia': 'Monrovia',
    'Africa/Lagos': 'West Central Africa',
    'Africa/Douala': 'West Central Africa',
    'Africa/Brazzaville': 'West Central Africa',
    'Africa/Kinshasa': 'West Central Africa',
    'Africa/Luanda': 'West Central Africa',
    'Africa/Cairo': 'Cairo',
    'Africa/Harare': 'Harare',
    'Africa/Maputo': 'Harare',
    'Africa/Windhoek': 'Harare',
    'Africa/Blantyre': 'Harare',
    'Africa/Bujumbura': 'Harare',
    'Africa/Gaborone': 'Harare',
    'Africa/Kigali': 'Harare',
    'Africa/Lubumbashi': 'Harare',
    'Africa/Lusaka': 'Harare',
    'Africa/Maseru': 'Harare',
    'Africa/Mbabane': 'Harare',
    'Africa/Johannesburg': 'Pretoria',
    'Africa/Nairobi': 'Nairobi',
    'Africa/Addis_Ababa': 'Nairobi',
    'Africa/Asmara': 'Nairobi',
    'Africa/Dar_es_Salaam': 'Nairobi',
    'Africa/Djibouti': 'Nairobi',
    'Africa/Juba': 'Nairobi',
    'Africa/Kampala': 'Nairobi',
    'Africa/Khartoum': 'Nairobi',
    'Africa/Mogadishu': 'Nairobi',

    // === EUROPE ===
    'Europe/Dublin': 'Dublin',
    'Europe/Belfast': 'Dublin',
    'Europe/Edinburgh': 'Edinburgh',
    'Europe/London': 'London',
    'Europe/Jersey': 'London',
    'Europe/Guernsey': 'London',
    'Europe/Isle_of_Man': 'London',
    'Europe/Lisbon': 'Lisbon',
    'Europe/Azores': 'Azores',
    'Europe/Madeira': 'Lisbon',
    UTC: 'UTC',
    GMT: 'UTC',

    // Central European Time
    'Europe/Amsterdam': 'Amsterdam',
    'Europe/Belgrade': 'Belgrade',
    'Europe/Berlin': 'Berlin',
    'Europe/Bern': 'Bern',
    'Europe/Zurich': 'Bern',
    'Europe/Bratislava': 'Bratislava',
    'Europe/Brussels': 'Brussels',
    'Europe/Budapest': 'Budapest',
    'Europe/Copenhagen': 'Copenhagen',
    'Europe/Ljubljana': 'Ljubljana',
    'Europe/Madrid': 'Madrid',
    'Europe/Paris': 'Paris',
    'Europe/Prague': 'Prague',
    'Europe/Rome': 'Rome',
    'Europe/Vatican': 'Rome',
    'Europe/San_Marino': 'Rome',
    'Europe/Sarajevo': 'Sarajevo',
    'Europe/Skopje': 'Skopje',
    'Europe/Stockholm': 'Stockholm',
    'Europe/Vienna': 'Vienna',
    'Europe/Warsaw': 'Warsaw',
    'Europe/Zagreb': 'Zagreb',

    // Eastern European Time
    'Europe/Athens': 'Athens',
    'Europe/Bucharest': 'Bucharest',
    'Europe/Helsinki': 'Helsinki',
    'Europe/Mariehamn': 'Helsinki',
    'Asia/Jerusalem': 'Jerusalem',
    'Asia/Gaza': 'Jerusalem',
    'Asia/Hebron': 'Jerusalem',
    'Europe/Kaliningrad': 'Kaliningrad',
    'Europe/Kiev': 'Kyiv',
    'Europe/Kyiv': 'Kyiv',
    'Europe/Uzhgorod': 'Kyiv',
    'Europe/Zaporozhye': 'Kyiv',
    'Europe/Riga': 'Riga',
    'Europe/Sofia': 'Sofia',
    'Europe/Tallinn': 'Tallinn',
    'Europe/Vilnius': 'Vilnius',

    // === ASIA & MIDDLE EAST ===
    // Moscow Time Zone
    'Asia/Baghdad': 'Baghdad',
    'Europe/Istanbul': 'Istanbul',
    'Asia/Istanbul': 'Istanbul',
    'Asia/Kuwait': 'Kuwait',
    'Europe/Minsk': 'Minsk',
    'Europe/Moscow': 'Moscow',
    'Europe/Simferopol': 'Moscow',
    'Asia/Riyadh': 'Riyadh',
    'Asia/Aden': 'Riyadh',
    'Asia/Bahrain': 'Riyadh',
    'Asia/Qatar': 'Riyadh',
    'Europe/Volgograd': 'Volgograd',
    'Europe/Kirov': 'Volgograd',
    'Europe/Astrakhan': 'Volgograd',
    'Europe/Saratov': 'Volgograd',
    'Europe/Ulyanovsk': 'Volgograd',

    // Iran
    'Asia/Tehran': 'Tehran',

    // Gulf States
    'Asia/Dubai': 'Abu Dhabi',
    'Asia/Baku': 'Baku',
    'Asia/Muscat': 'Muscat',
    'Europe/Samara': 'Samara',
    'Asia/Tbilisi': 'Tbilisi',
    'Asia/Yerevan': 'Yerevan',

    // Afghanistan
    'Asia/Kabul': 'Kabul',

    // Central Asia
    'Asia/Almaty': 'Almaty',
    'Asia/Qyzylorda': 'Almaty',
    'Asia/Aqtobe': 'Almaty',
    'Asia/Aqtau': 'Almaty',
    'Asia/Atyrau': 'Almaty',
    'Asia/Oral': 'Almaty',
    'Asia/Qostanay': 'Astana',
    'Asia/Yekaterinburg': 'Ekaterinburg',
    'Asia/Islamabad': 'Islamabad',
    'Asia/Karachi': 'Karachi',
    'Asia/Tashkent': 'Tashkent',
    'Asia/Samarkand': 'Tashkent',

    // Indian Subcontinent
    'Asia/Chennai': 'Chennai',
    'Asia/Kolkata': 'Kolkata',
    'Asia/Calcutta': 'Kolkata',
    'Asia/Mumbai': 'Mumbai',
    'Asia/Bombay': 'Mumbai',
    'Asia/Delhi': 'New Delhi',
    'Asia/Colombo': 'Sri Jayawardenepura',
    'Asia/Kathmandu': 'Kathmandu',
    'Asia/Katmandu': 'Kathmandu',

    // Bangladesh
    'Asia/Dhaka': 'Dhaka',
    'Asia/Dacca': 'Dhaka',
    'Asia/Urumqi': 'Urumqi',

    // Southeast Asia
    'Asia/Rangoon': 'Rangoon',
    'Asia/Yangon': 'Rangoon',
    'Asia/Bangkok': 'Bangkok',
    'Asia/Phnom_Penh': 'Bangkok',
    'Asia/Vientiane': 'Bangkok',
    'Asia/Hanoi': 'Hanoi',
    'Asia/Ho_Chi_Minh': 'Hanoi',
    'Asia/Saigon': 'Hanoi',
    'Asia/Jakarta': 'Jakarta',
    'Asia/Pontianak': 'Jakarta',
    'Asia/Krasnoyarsk': 'Krasnoyarsk',
    'Asia/Novokuznetsk': 'Krasnoyarsk',
    'Asia/Novosibirsk': 'Novosibirsk',
    'Asia/Barnaul': 'Novosibirsk',
    'Asia/Tomsk': 'Novosibirsk',

    // East Asia
    'Asia/Shanghai': 'Beijing',
    'Asia/Beijing': 'Beijing',
    'Asia/Harbin': 'Beijing',
    'Asia/Chongqing': 'Beijing',
    'Asia/Chungking': 'Chongqing',
    'Asia/Hong_Kong': 'Hong Kong',
    'Asia/Irkutsk': 'Irkutsk',
    'Asia/Kuala_Lumpur': 'Kuala Lumpur',
    'Asia/Kuching': 'Kuala Lumpur',
    'Australia/Perth': 'Perth',
    'Asia/Singapore': 'Singapore',
    'Asia/Taipei': 'Taipei',
    'Asia/Ulaanbaatar': 'Ulaanbaatar',
    'Asia/Ulan_Bator': 'Ulaanbaatar',
    'Asia/Choibalsan': 'Ulaanbaatar',
    'Asia/Hovd': 'Ulaanbaatar',

    // Japan & Korea
    'Asia/Sapporo': 'Sapporo',
    'Asia/Seoul': 'Seoul',
    'Asia/Tokyo': 'Tokyo',
    Japan: 'Tokyo',
    'Asia/Yakutsk': 'Yakutsk',
    'Asia/Chita': 'Yakutsk',
    'Asia/Khandyga': 'Yakutsk',

    // === AUSTRALIA & OCEANIA ===
    'Australia/Adelaide': 'Adelaide',
    'Australia/Broken_Hill': 'Adelaide',
    'Australia/Darwin': 'Darwin',
    'Australia/Brisbane': 'Brisbane',
    'Australia/Lindeman': 'Brisbane',
    'Australia/Canberra': 'Canberra',
    'Australia/ACT': 'Canberra',
    'Pacific/Guam': 'Guam',
    'Pacific/Saipan': 'Guam',
    'Australia/Hobart': 'Hobart',
    'Australia/Currie': 'Hobart',
    'Australia/Melbourne': 'Melbourne',
    'Australia/Victoria': 'Melbourne',
    'Pacific/Port_Moresby': 'Port Moresby',
    'Pacific/Bougainville': 'Port Moresby',
    'Australia/Sydney': 'Sydney',
    'Australia/NSW': 'Sydney',
    'Asia/Vladivostok': 'Vladivostok',
    'Asia/Ust-Nera': 'Vladivostok',
    'Asia/Sakhalin': 'Vladivostok',

    // Pacific +11
    'Asia/Magadan': 'Magadan',
    'Pacific/Noumea': 'New Caledonia',
    'Pacific/Guadalcanal': 'Solomon Is.',
    'Pacific/Ponape': 'Solomon Is.',
    'Pacific/Kosrae': 'Solomon Is.',
    'Pacific/Efate': 'Solomon Is.',
    'Asia/Srednekolymsk': 'Srednekolymsk',

    // Pacific +12
    'Pacific/Auckland': 'Auckland',
    'Pacific/Fiji': 'Fiji',
    'Asia/Kamchatka': 'Kamchatka',
    'Asia/Anadyr': 'Kamchatka',
    'Pacific/Majuro': 'Marshall Is.',
    'Pacific/Kwajalein': 'Marshall Is.',
    'Pacific/Wellington': 'Wellington',

    // Pacific +12:45
    'Pacific/Chatham': 'Chatham Is.',

    // Pacific +13
    'Pacific/Tongatapu': "Nuku'alofa",
    'Pacific/Apia': 'Samoa',
    'Pacific/Fakaofo': 'Tokelau Is.',
  };

  const railsName = conversionMap[ianaName];
  console.log('IANA to Rails conversion:', ianaName, '->', railsName || 'NO MAPPING');
  return railsName || ianaName;
};

// Alternative name for the same function to match your component imports
export const convertIanaToRails = convertIANAToRails;

// Create timezone options (alternative name for processRailsTimezoneData)
export const createTimezoneOptionsFromRails = processRailsTimezoneData;

// Check if a timezone value is valid in the Rails timezone list
export const isValidRailsTimezone = (railsTimezones: RailsTimezoneData[], value: string): boolean => {
  if (!railsTimezones || !Array.isArray(railsTimezones) || !value) {
    return false;
  }

  return railsTimezones.some((tz) => tz.value === value);
};
